import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { GameCard } from "./GameCard";
import type { CatalogItem, SortMode } from "../lib/types";

// Geometry from the original .index grid (styles.css): cards are minmax(250px,1fr)
// with an 8px (--space-2) gap and ~91px tall. We virtualize by ROW against the
// window scroll, so only the visible rows (+overscan) are ever in the DOM.
const MIN_COL = 250;
const GAP = 8;
const ROW_H = 91 + GAP;

interface Props {
  items: CatalogItem[];
  sort: SortMode;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  emptyMessage: string;
}

export function CatalogGrid(props: Props) {
  const { items } = props;
  const listRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [offset, setOffset] = useState(0);

  // Track the list's width (→ columns) and its document offset (→ scrollMargin).
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const measure = () => {
      setWidth(el.clientWidth);
      setOffset(el.getBoundingClientRect().top + window.scrollY);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const columns = width > 0 ? Math.max(1, Math.floor((width + GAP) / (MIN_COL + GAP))) : 1;
  const rowCount = Math.ceil(items.length / columns);

  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => ROW_H,
    overscan: 4,
    scrollMargin: offset,
  });

  const virtualRows = virtualizer.getVirtualItems();

  // Pull the next page as the final row scrolls into view (also auto-fills the
  // first viewport when a page is shorter than the screen).
  useEffect(() => {
    const last = virtualRows[virtualRows.length - 1];
    if (last && last.index >= rowCount - 1 && props.hasNextPage && !props.isFetchingNextPage) {
      props.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualRows, rowCount, props.hasNextPage, props.isFetchingNextPage]);

  if (props.isError) return <p className="empty">The catalog could not be read.</p>;
  if (!items.length && !props.isLoading) return <p className="empty">{props.emptyMessage}</p>;

  return (
    <div ref={listRef} style={{ position: "relative", paddingBottom: 48 }}>
      <div style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}>
        {virtualRows.map((vr) => {
          const start = vr.index * columns;
          const rowItems = items.slice(start, start + columns);
          return (
            <div
              key={vr.key}
              data-index={vr.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${vr.start - virtualizer.options.scrollMargin}px)`,
                display: "grid",
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap: GAP,
              }}
            >
              {rowItems.map((item) => (
                <GameCard key={`${item.catalog}:${item.id}`} item={item} sort={props.sort} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
